import { cn, useTranslation } from '@hbs-us/ui';
import { Bell } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { NotificationCenter } from '../../features/notifications/NotificationCenter.js';
import { useNotificationSocket } from '../../features/notifications/useNotificationSocket.js';
import { useUnreadCountQuery } from '../../features/notifications/useNotificationsQuery.js';

/**
 * The top-bar notification bell (Surface C-web — D-13/D-15). A real `<button>` whose accessible name
 * carries the live unread count (`aria-label="Notifications, {n} unread"` — the count is in the
 * NAME, never conveyed by the red badge color alone, A11Y-01). The `--destructive` red count badge
 * renders "99+" past 99 so it never overflows. Clicking opens a `NotificationCenter` popover;
 * Escape closes it and returns focus to the bell; a click outside closes it. The 44px hit area is
 * the bell itself (`h-11 w-11`). Token-driven, zero hardcoded hex.
 *
 * Live (NOTIF-05 / D-15): the bell mounts `useNotificationSocket` — the WebSocket layer pushes live
 * arrivals into the same query cache the ~30s poll feeds, so the unread count + list update the
 * instant a notification is sent. When the socket drops, the poll silently keeps the bell fresh —
 * NO chrome. The bell hosts the SINGLE `aria-live="polite"` arrival announcer (always mounted, so a
 * burst is announced even with the panel closed); the socket hook debounces a burst into one
 * "{n} new notifications" string. The unread count itself stays server truth.
 */

const MAX_BADGE = 99;

export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const { data: unread = 0 } = useUnreadCountQuery();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // The single debounced polite announcement for a live arrival burst ("{n} new notifications"),
  // localized. The socket hook collapses a burst into ONE call; the count drives the locale-correct
  // string. This is the ONLY user-visible signal of liveness — socket up/down is never chrome.
  const announce = useCallback((count: number) => t('center.arrivalBurst', { count }), [t]);
  const { arrivalAnnouncement } = useNotificationSocket(announce);

  // Escape closes + returns focus to the bell; a click outside closes (A11Y-01 dialog hygiene).
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        bellRef.current?.focus();
      }
    }
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        bellRef.current &&
        !bellRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const badgeLabel = unread > MAX_BADGE ? `${MAX_BADGE}+` : String(unread);

  return (
    <div className="relative">
      <button
        ref={bellRef}
        type="button"
        aria-label={t('bell.ariaLabel', { count: unread })}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        /*
          The bell wears the SAME resting ink and hover as every other icon button in
          either top bar. It used to sit at `text-foreground` over a 60%-orange hover
          while the launcher, search and account trigger beside it sat at
          `text-muted-foreground` over a neutral hover — three inks in one 64px row,
          and an accent used to mean "the pointer is here" when everywhere else in
          this product it means "you are here" or "this is the action". The bar it
          sits on is `bg-card` in BOTH shells, so that is what the ring gap names.
        */
        className="relative inline-flex size-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        <Bell className="size-5" aria-hidden="true" />
        {unread > 0 ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1 right-1 inline-flex min-w-4 items-center justify-center rounded-full bg-destructive px-1',
              'font-mono font-semibold text-[0.625rem] text-destructive-foreground leading-none',
            )}
          >
            {badgeLabel}
          </span>
        ) : null}
      </button>

      {/*
        The SINGLE polite arrival announcer (NOTIF-05 / D-15 / A11Y-01). Always mounted on the bell
        (not the panel), so a live burst is announced even when the center is closed. The socket hook
        debounces a burst into one localized "{n} new notifications" string; never assertive, never
        per-item. Visually hidden — the visible change is the badge/list, this is the SR-only echo.
      */}
      <div aria-live="polite" className="sr-only" data-testid="notification-arrival">
        {arrivalAnnouncement}
      </div>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={t('center.heading')}
          className="absolute right-0 z-50 mt-2 overflow-hidden rounded-md border border-border bg-card shadow-lg"
        >
          <NotificationCenter />
        </div>
      ) : null}
    </div>
  );
}
